"use client";

import React from "react";
import { ResponsiveModal, useGlobalModalStack } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { Settings } from "lucide-react";
import { useColumnManager } from "@/lib/column-manager";
import { MANAGE_TABLE_COLUMNS_MODAL_ID } from "./constants";
import { ColumnManagerPanel } from "./components";

interface SimpleColumnManagerProps {
  trigger?: React.ReactNode;
}

/**
 * SimpleColumnManager — trigger button + modal shell around the two-pane
 * ColumnManagerPanel (left: grouped + searchable "available"; right: draggable
 * "shown" order). The panel holds all the interactive logic and is unit-tested
 * directly; this file only owns the trigger and the ResponsiveModal wrapper.
 */
export function SimpleColumnManager({ trigger }: SimpleColumnManagerProps) {
  const { openModal, closeModal } = useGlobalModalStack();
  const [isMounted, setIsMounted] = React.useState(false);

  const { visibleColumns, isLoading } = useColumnManager();

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  if (!isMounted || isLoading) {
    return null;
  }

  const defaultTrigger = (
    <Button variant="outline" size="sm" className="gap-2">
      <Settings size={16} />
      Columns ({visibleColumns.length})
    </Button>
  );

  return (
    <>
      <div onClick={() => openModal(MANAGE_TABLE_COLUMNS_MODAL_ID)}>{trigger || defaultTrigger}</div>

      <ResponsiveModal
        globalModalId={MANAGE_TABLE_COLUMNS_MODAL_ID}
        title="Manage Columns"
        maxWidth="lg"
      >
        <ColumnManagerPanel onDone={() => closeModal(MANAGE_TABLE_COLUMNS_MODAL_ID)} />
      </ResponsiveModal>
    </>
  );
}
